import { Injectable, InjectionToken, Provider } from '@angular/core';
import { UserListQueryParams } from '../../features/user/user-list/user-list-request';
import { possibleRepresentationMocks, representationMocks } from '../../mock-data/user.mock';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import { TransferRequestItem } from '../api-types/client.api';
import { ApiRepresentation } from '../api-types/representation.api';
import {
	ApiCanDeleteUserValidationResponse,
	ApiUser,
	CreateApiUserBody,
	UpdateApiUserBody,
} from '../api-types/user.api';
import { UserClient } from '../app-types/client.app';
import { Representation } from '../app-types/representation.app';
import { User, UserClientsQuery } from '../app-types/user.app';
import { transformApiRepresentationsToAppRepresentations } from '../transformers/representation.transform';
import {
	transformApiCanDeleteUserValidationToAppCanDeleteUserValidation,
	transformApiUsersToAppUsers,
	transformApiUserToAppUser,
	transformUpdateUserToApiUserBody,
} from '../transformers/user.transform';
import { BaseRequestService } from './base-request.service';

export const USER_SERVICE = new InjectionToken<UserApi>('UserApi');

/**
 * `providedIn: 'root'` gives `UserApi` a root-accessible default instance (injectable via the bare
 * class token) that `CurrentUserService` uses to fetch/cache `users/me` exactly once for the whole
 * app. This does NOT change any existing `USER_SERVICE`-token based provider registrations
 * (`provideUserApi` / `{ provide: USER_SERVICE, useClass: UserApi }`) scattered across routes and
 * components - those keep creating their own separate instances as before, for their own unrelated
 * use cases.
 */
@Injectable({ providedIn: 'root' })
export class UserApi extends BaseRequestService {
	public getSingleMe = async () => {
		const apiUser = await this.get<ApiUser>('users/me', undefined, { skipActAs: true });
		const user = transformApiUserToAppUser(apiUser);
		return user;
	};

	public getCanDeleteUser = async (userId: string) => {
		const result = await this.get<ApiCanDeleteUserValidationResponse>(`validation/canDeleteUser/${userId}`);
		return transformApiCanDeleteUserValidationToAppCanDeleteUserValidation(result);
	};

	public getAll = async (params: UserListQueryParams): Promise<PaginatedResponse<User>> => {
		const apiResponse = await this.get<PaginatedResponse<ApiUser>>('users', {
			offset: params.offset,
			range: params.range,
			activeUsers: params.activeUsers,
			sortBy: params.sortBy,
			ascending: params.ascending,

			...(params.firstName ? { firstName: params.firstName } : {}),

			...(params.name ? { name: params.name } : {}),

			...(params.email ? { email: params.email } : {}),
		});

		return {
			entities: transformApiUsersToAppUsers(apiResponse.entities),
			offset: apiResponse.offset,
			range: apiResponse.range,
			totalEntities: apiResponse.totalEntities,
		};
	};

	public getRepresentations = async (userId: string, offset?: number, range?: number) => {
		const apiResponse = await this.get<PaginatedResponse<ApiRepresentation>>(
			`users/${userId}/representations`,
			{
				offset,
				range,
			},
			{
				skipActAs: true,
			},
		);
		const representations = transformApiRepresentationsToAppRepresentations(apiResponse.entities);
		const page: PaginatedResponse<Representation> = {
			entities: representations,
			offset: apiResponse.offset,
			range: apiResponse.range,
			totalEntities: apiResponse.totalEntities,
		};
		return page;
	};

	public getBySpecialization = async (categoryTypeOfCareId: number, offset?: number, range?: number) => {
		const apiResponse = await this.get<PaginatedResponse<ApiUser>>(`users/bySpecialization/${categoryTypeOfCareId}`, {
			offset: offset ?? 0,
			range: range ?? 100,
		});
		const users = transformApiUsersToAppUsers(apiResponse.entities);
		const page: PaginatedResponse<User> = {
			entities: users,
			offset: apiResponse.offset,
			range: apiResponse.range,
			totalEntities: apiResponse.totalEntities,
		};
		return page;
	};

	public getSingle = async (id: string) => {
		const apiUser = await this.get<ApiUser>(`users/${id}`);
		const user = transformApiUserToAppUser(apiUser);
		return user;
	};

	public updateMe = async (data: UpdateApiUserBody) => {
		const body = transformUpdateUserToApiUserBody(data);
		return await this.put<UpdateApiUserBody, UpdateApiUserBody>('users/me', body);
	};

	public update = async (id: string, data: UpdateApiUserBody) => {
		const body = transformUpdateUserToApiUserBody(data);
		return await this.put<UpdateApiUserBody, UpdateApiUserBody>(`users/${id}`, body);
	};

	public async create(body: CreateApiUserBody): Promise<CreateApiUserBody> {
		const response = await this.post<CreateApiUserBody, CreateApiUserBody>('users', body);
		return response;
	}

	public async deactivateUser(userId: string) {
		await this.deactivate<void>(`users/deactivate/${userId}`);
		return;
	}

	public async deleteUser(userId: string) {
		await this.remove<void>(`users/${userId}`);
		return;
	}

	public async reactivateUser(userId: string) {
		await this.post<void, void>(`users/reactivate/${userId}`, undefined);
		return;
	}

	public async getUserClients(query: UserClientsQuery) {
		const response = await this.get<PaginatedResponse<UserClient>>('/users/clientView', {
			clientNameFilter: query.clientNameFilter,
			ownerNameFilter: query.ownerNameFilter,
			assignmentGuidFilter: query.assignmentGuidFilter,
			userIdToExclude: query.userIdToExclude,
			offset: query.offset,
			range: query.range,
		});
		const clients = response.entities;
		const page: PaginatedResponse<UserClient> = {
			entities: clients,
			offset: response.offset,
			range: response.range,
			totalEntities: response.totalEntities,
		};
		return page;
	}

	public transferAssignments(targetUserId: number | string, body: { request: TransferRequestItem[] }) {
		return this.post<void, { request: TransferRequestItem[] }>(`users/${targetUserId}/assignments/transfer`, body);
	}

	public async getPossibleRepresentations(userId: string, offset?: number, range?: number) {
		const apiResponse = await this.get<PaginatedResponse<ApiRepresentation>>(
			`users/${userId}/possibleRepresentations`,
			{
				offset,
				range,
			},
			{ skipActAs: true },
		);
		const representations = transformApiRepresentationsToAppRepresentations(apiResponse.entities);
		const page: PaginatedResponse<Representation> = {
			entities: representations,
			offset: apiResponse.offset,
			range: apiResponse.range,
			totalEntities: apiResponse.totalEntities,
		};
		return page;
	}

	public async updateRepresentations(userId: string, userIds: string[]) {
		await this.post<void, string[]>(`users/${userId}/representations`, userIds, undefined, { skipActAs: true });
	}
}

@Injectable()
export class UserMockApi extends BaseRequestService {
	private representations: Representation[] = [...representationMocks];
	private readonly possibleRepresentations: Representation[] = [...possibleRepresentationMocks];

	public constructor() {
		super();
		console.warn('⛔️⛔️⛔️ User Mock Service: You are currently using a mock service!');
	}

	public async getRepresentations(_userId: string, offset?: number, range?: number) {
		const entities = this.representations;
		const page: PaginatedResponse<Representation> = {
			entities,
			offset: offset ?? 0,
			range: range ?? entities.length,
			totalEntities: entities.length,
		};
		return page;
	}

	public async getPossibleRepresentations(_userId: string, offset?: number, range?: number) {
		const entities = this.possibleRepresentations;
		const page: PaginatedResponse<Representation> = {
			entities,
			offset: offset ?? 0,
			range: range ?? entities.length,
			totalEntities: entities.length,
		};
		return page;
	}

	public async updateRepresentations(_userId: string, userIds: number[]) {
		const idSet = new Set(userIds.map(String));
		this.representations = this.possibleRepresentations.filter((user) => idSet.has(user.id));
	}
}

export const provideUserApi: Provider = {
	provide: USER_SERVICE,
	useClass: UserApi,
};
